import React, { useEffect, useState, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  getDocs,
  query,
  where,
  updateDoc,
  doc
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../firebaseConfig';
import menuOptions from '../config/menuOptions.json';

const defaultMenus = menuOptions;

function MenuChoices() {
  // Deadline for editing menu choices
  const deadline = new Date('2026-03-27T23:59:59'); // example deadline
  const now = new Date();
  const isPastDeadline = now > deadline;
  const [familyGuests, setFamilyGuests] = useState([]);
  const [allGuests, setAllGuests] = useState([]);
  const [selectedClan, setSelectedClan] = useState(null);
  const [availableClans, setAvailableClans] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dietaryRequirementsInput, setDietaryRequirementsInput] = useState({});
  const fileInputRefs = useRef({});
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const fetchGuests = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user?.phoneNumber) return;

      const invitesRef = collection(db, 'invites');
      const userSnap = await getDocs(query(invitesRef, where('phoneNumber', '==', user.phoneNumber)));
      if (userSnap.empty) return;

      const userGuest = userSnap.docs[0].data();
      const userRole = userGuest.role?.toLowerCase();
      setCurrentUserRole(userRole);

      // If user is groom or bride, fetch all guests
      if (userRole === 'groom' || userRole === 'bride') {
        const allGuestsSnap = await getDocs(invitesRef);
        const allGuestsList = allGuestsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllGuests(allGuestsList);
        
        // Get unique clans
        const uniqueClans = [...new Set(allGuestsList.map(g => g.familyClan).filter(Boolean))].sort();
        setAvailableClans(uniqueClans);
        
        // Set first clan as default selection
        if (uniqueClans.length > 0) {
          setSelectedClan(uniqueClans[0]);
          const firstClanGuests = allGuestsList.filter(g => g.familyClan === uniqueClans[0]);
          setFamilyGuests(firstClanGuests);
        }
      } else {
        // Otherwise, fetch only family guests
        const clan = userGuest.familyClan;
        const clanSnap = await getDocs(query(invitesRef, where('familyClan', '==', clan)));
        const guests = clanSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFamilyGuests(guests);
      }
      setLoading(false);
    };

    fetchGuests();
  }, []);

  // Update displayed guests when clan selection changes (for groom/bride)
  useEffect(() => {
    if ((currentUserRole === 'groom' || currentUserRole === 'bride') && selectedClan && allGuests.length > 0) {
      const clanGuests = allGuests.filter(g => g.familyClan === selectedClan);
      setFamilyGuests(clanGuests);
    }
  }, [selectedClan, allGuests, currentUserRole]);

  const handleMenuChange = async (guestId, course, value) => {
    const guestRef = doc(db, 'invites', guestId);
    await updateDoc(guestRef, {
      [`menuChoices.${course}`]: value
    });
    setFamilyGuests(prev =>
      prev.map(g => g.id === guestId ? {
        ...g,
        menuChoices: {
          ...g.menuChoices,
          [course]: value
        }
      } : g)
    );
    // Also update allGuests if in groom/bride mode
    if (currentUserRole === 'groom' || currentUserRole === 'bride') {
      setAllGuests(prev =>
        prev.map(g => g.id === guestId ? {
          ...g,
          menuChoices: {
            ...g.menuChoices,
            [course]: value
          }
        } : g)
      );
    }
  };

  const handleDietaryRequirementsSubmit = async (guestId) => {
    const input = dietaryRequirementsInput[guestId];
    // If no input state, use current guest values
    const guest = familyGuests.find(g => g.id === guestId);
    const hasRequirements = input?.hasRequirements ?? guest?.hasDietaryRequirements ?? false;
    const requirements = (input?.requirements ?? guest?.dietaryRequirements ?? '').trim();
    
    console.log('Saving dietary requirements:', { guestId, hasRequirements, requirements, input });
    
    try {
      const guestRef = doc(db, 'invites', guestId);
      const updateData = {
        hasDietaryRequirements: hasRequirements,
        dietaryRequirements: hasRequirements ? requirements : ''
      };
      console.log('Update data:', updateData);
      await updateDoc(guestRef, updateData);
      console.log('Successfully saved dietary requirements');
      
      setFamilyGuests(prev =>
        prev.map(g => g.id === guestId ? {
          ...g,
          hasDietaryRequirements: hasRequirements,
          dietaryRequirements: hasRequirements ? requirements : ''
        } : g)
      );
      
      // Also update allGuests if in groom/bride mode
      if (currentUserRole === 'groom' || currentUserRole === 'bride') {
        setAllGuests(prev =>
          prev.map(g => g.id === guestId ? {
            ...g,
            hasDietaryRequirements: hasRequirements,
            dietaryRequirements: hasRequirements ? requirements : ''
          } : g)
        );
      }
      
      // Clear the input state for this guest
      setDietaryRequirementsInput(prev => {
        const updated = { ...prev };
        delete updated[guestId];
        return updated;
      });
      
      alert('Dietary requirements saved successfully!');
    } catch (error) {
      console.error('Error saving dietary requirements:', error);
      alert('Failed to save dietary requirements. Please try again.');
    }
  };

  const updateDietaryRequirementsInput = (guestId, hasRequirements, requirements) => {
    setDietaryRequirementsInput(prev => ({
      ...prev,
      [guestId]: {
        hasRequirements,
        requirements: requirements || ''
      }
    }));
  };

  const handlePhotoUpload = async (guestId, file) => {
    if (!file) return;

    try {
      const storage = getStorage();
      const photoRef = ref(storage, `guest_photos/${guestId}`);
      await uploadBytes(photoRef, file);
      const url = await getDownloadURL(photoRef);

      // Update Firestore
      const guestRef = doc(db, 'invites', guestId);
      await updateDoc(guestRef, { photoUrl: url });

      // Update local state
      setFamilyGuests(prev =>
        prev.map(g => (g.id === guestId ? { ...g, photoUrl: url } : g))
      );
      
      // Also update allGuests if in groom/bride mode
      if (currentUserRole === 'groom' || currentUserRole === 'bride') {
        setAllGuests(prev =>
          prev.map(g => (g.id === guestId ? { ...g, photoUrl: url } : g))
        );
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
  };

  if (loading) return <div style={{ 
    textAlign: 'center', 
    marginTop: '40px',
    padding: '0 20px',
    fontSize: 'clamp(0.9em, 2.5vw, 1em)',
    color: '#fff'
  }}>Loading guests...</div>;

  const isGroomOrBride = currentUserRole === 'groom' || currentUserRole === 'bride';
  // Only show day guests for menu selection (evening guests don't have meals) — for both admin and regular users
  const dayGuests = familyGuests.filter(g => !g.eveningGuest);
  const isEveningOnlyFamily = !isGroomOrBride && dayGuests.length === 0;

  return (
    <>
      <style>{`
        @media (min-width: 600px) {
          .menu-options-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 600px) {
          .menu-options-grid .card[style*="grid-column"] {
            grid-column: 1 / -1 !important;
          }
        }
        @keyframes slideUpFromBottom {
          0% {
            transform: translateX(-50%) translateY(100px);
            opacity: 0;
          }
          100% {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div className="page-container">
      <h2 className="page-title">Menu Choices</h2>
      {isEveningOnlyFamily ? (
        <div className="page-subtitle" style={{ marginBottom: '30px', textAlign: 'center', padding: '20px' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'clamp(1rem, 2.5vw, 1.15rem)', color: 'var(--text-primary)' }}>
            You're joining us for the evening reception only—no meal selection is needed. We look forward to seeing you!
          </p>
        </div>
      ) : (
      <>
      <div className="page-subtitle" style={{ marginBottom: '30px' }}>
        <p style={{ marginBottom: '8px' }}>
          Please select your preferred starter, main course, and dessert for each guest in your party.
        </p>
        {!isPastDeadline ? (
          <p style={{ fontWeight: '600', fontFamily: 'var(--font-body)' }}>
            Please submit your choices by {deadline.toLocaleDateString('en-GB', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}.
          </p>
        ) : (
          <p style={{ fontWeight: '600', color: 'var(--button-danger)', fontStyle: 'italic', fontFamily: 'var(--font-body)' }}>
            The deadline for menu choices has passed. Your selections are now locked.
          </p>
        )}
      </div>
      {isGroomOrBride && (
        <div style={{
          marginBottom: '30px',
          textAlign: 'center',
          padding: '0 10px'
        }}>
          <label style={{
            display: 'block',
            marginBottom: '10px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            fontSize: 'clamp(0.95em, 3vw, 1.1em)',
            fontFamily: 'var(--font-body)'
          }}>
            Select Clan:
          </label>
          <select
            value={selectedClan || ''}
            onChange={e => setSelectedClan(e.target.value)}
            className="select-field"
            style={{
              maxWidth: '400px',
              margin: '0 auto',
              display: 'block'
            }}
          >
            <option value="" disabled>Choose a clan...</option>
            {availableClans.map(clan => {
              const clanGuests = allGuests.filter(g => g.familyClan === clan);
              const firstAdult = clanGuests.find(g => g.ageCategory?.toLowerCase() === 'adult');
              const adultName = firstAdult ? ` - ${firstAdult.name}` : '';
              const guestCount = clanGuests.length;
              return (
                <option key={clan} value={clan}>
                  {clan}{adultName} ({guestCount} {guestCount === 1 ? 'guest' : 'guests'})
                </option>
              );
            })}
          </select>
        </div>
      )}
      {!isPastDeadline && (
        <div style={{ marginBottom: '40px', padding: '0 10px' }}>
          <h3 className="page-subtitle" style={{ 
            marginBottom: '20px',
            fontSize: 'clamp(1.2rem, 3.5vw, 1.5rem)',
            fontFamily: 'var(--font-heading)'
          }}>Full Menu Options</h3>
          <div className="menu-options-grid" style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '20px',
            maxWidth: '660px',
            margin: '0 auto',
            justifyContent: 'center'
          }}>
            {(() => {
              const hasChildrenOrTeens = dayGuests.some(g => {
                const age = (g.ageCategory || '').toLowerCase();
                return age !== 'adult' && age !== 'infant';
              });
              const menusToShow = hasChildrenOrTeens 
                ? Object.entries(defaultMenus) 
                : Object.entries(defaultMenus).filter(([age]) => age === 'Adult');
              const isAdultOnly = !hasChildrenOrTeens;
              
              return menusToShow.map(([age, courses]) => (
              <div 
                key={age} 
                className="card"
                style={isAdultOnly ? {
                  gridColumn: '1 / -1',
                  maxWidth: '100%'
                } : {}}
              >
                <h4 style={{ 
                  textAlign: 'center', 
                  marginBottom: '10px',
                  fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                  fontFamily: 'var(--font-heading)'
                }}>{age} Menu</h4>
                <p style={{
                  textAlign: 'center',
                  marginBottom: '15px',
                  fontSize: 'clamp(0.8em, 2.2vw, 0.9em)',
                  fontStyle: 'italic',
                  color: 'var(--text-primary)',
                  padding: '0 10px',
                  fontFamily: 'var(--font-body)'
                }}>
                  {age === 'Adult' 
                    ? 'This menu is recommended for all guests aged 12+ years'
                    : 'This menu is recommended for children up to 12 years old, however if your child would prefer an age appropriate portion from the main menu this is also available in the selection below'
                  }
                </p>
                {Object.entries(courses).map(([course, items]) => (
                  <div key={course} style={{ marginBottom: '10px' }}>
                    <strong style={{ 
                      textTransform: 'capitalize',
                      fontSize: 'clamp(0.9em, 2.5vw, 1em)'
                    }}>{course}:</strong>
                    <ul style={{ 
                      marginTop: '4px', 
                      paddingLeft: '20px',
                      fontSize: 'clamp(0.8em, 2.2vw, 0.9em)'
                    }}>
                      {items.map((item, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>
                          <strong>{item.name}</strong>: <span style={{ fontSize: '0.9em' }}>{item.description}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              ));
            })()}
          </div>
        </div>
      )}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        justifyContent: 'center',
        padding: '0 10px'
      }}>
        {dayGuests
          .filter(guest => (guest.ageCategory || '').toLowerCase() !== 'infant')
          .map(guest => {
          const ageCategory = (guest.ageCategory || '').toLowerCase();
          const isChild = ageCategory === 'child';
          const isTeen = ageCategory === 'teen';
          const isAdult = ageCategory === 'adult';
          
          // Determine which menu to use
          // Teens see adult menu options (but will get smaller portions)
          // Adults see adult menu options
          // Children can select from both child and adult menu options
          let menu;
          if (isTeen || isAdult) {
            menu = defaultMenus['Adult'];
          } else {
            menu = defaultMenus['Child'];
          }
          
          // Children can select from both child and adult menu options
          const getMenuOptions = (course) => {
            if (isChild) {
              // Combine child and adult options, with child options first
              const childOptions = defaultMenus['Child'][course] || [];
              const adultOptions = defaultMenus['Adult'][course] || [];
              return [...childOptions, ...adultOptions];
            } else {
              // Teens and adults see only adult menu options
              return menu[course] || [];
            }
          };
          
          return (
            <div key={guest.id} className="card" style={{
              flex: '1 1 280px',
              maxWidth: '320px',
              minWidth: '280px',
              textAlign: 'center'
            }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={guest.photoUrl || 'https://placehold.co/100'}
                  alt={guest.name}
                  onClick={() => fileInputRefs.current[guest.id]?.click()}
                  style={{
                    width: 'clamp(80px, 20vw, 100px)',
                    height: 'clamp(80px, 20vw, 100px)',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    marginBottom: '10px',
                    border: '2px solid white',
                    cursor: 'pointer',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                  title="Click to update photo"
                />
                <input
                  ref={el => fileInputRefs.current[guest.id] = el}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handlePhotoUpload(guest.id, file);
                    }
                    // Reset input so same file can be selected again
                    e.target.value = '';
                  }}
                />
              </div>
              <h3 style={{ 
                marginBottom: '10px',
                fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                fontFamily: 'var(--font-heading)'
              }}>{guest.name}</h3>
              {['starter', 'main', 'dessert'].map(course => (
                <div key={course} style={{ marginBottom: '15px', textAlign: 'left' }}>
                  <label style={{ 
                    marginRight: '10px', 
                    fontWeight: 'bold',
                    fontSize: 'clamp(0.85em, 2.5vw, 0.95em)',
                    display: 'block',
                    marginBottom: '5px',
                    fontFamily: 'var(--font-body)'
                  }}>{course.charAt(0).toUpperCase() + course.slice(1)}:</label>
                  {isPastDeadline ? (
                    <div style={{ 
                      fontStyle: 'italic', 
                      paddingTop: '6px',
                      fontSize: 'clamp(0.85em, 2.5vw, 0.95em)',
                      wordBreak: 'break-word',
                      fontFamily: 'var(--font-body)'
                    }}>
                      {guest.menuChoices?.[course] || 'No choice selected'}
                    </div>
                  ) : (
                    <select
                      value={guest.menuChoices?.[course] || ''}
                      onChange={e => handleMenuChange(guest.id, course, e.target.value)}
                      className="select-field"
                    >
                      <option value="" disabled>Select {course}</option>
                      {getMenuOptions(course).map((item, i) => (
                        <option key={i} value={item.name}>{item.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
              <div style={{ marginTop: '20px', marginBottom: '15px', textAlign: 'left' }}>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '10px',
                  fontSize: 'clamp(0.85em, 2.5vw, 0.95em)',
                  fontFamily: 'var(--font-body)',
                  fontWeight: 'bold'
                }}>
                  <input
                    type="checkbox"
                    checked={(dietaryRequirementsInput[guest.id]?.hasRequirements ?? guest.hasDietaryRequirements) || false}
                    onChange={e => updateDietaryRequirementsInput(guest.id, e.target.checked, dietaryRequirementsInput[guest.id]?.requirements || guest.dietaryRequirements || '')}
                    disabled={isPastDeadline}
                    style={{ 
                      marginRight: '8px',
                      width: '18px',
                      height: '18px',
                      cursor: isPastDeadline ? 'not-allowed' : 'pointer'
                    }}
                  />
                  I have dietary requirements
                </label>
                {((dietaryRequirementsInput[guest.id]?.hasRequirements ?? guest.hasDietaryRequirements) || (isPastDeadline && guest.dietaryRequirements)) && (
                  <>
                    <textarea
                      value={(dietaryRequirementsInput[guest.id]?.requirements ?? guest.dietaryRequirements) || ''}
                      onChange={e => updateDietaryRequirementsInput(guest.id, (dietaryRequirementsInput[guest.id]?.hasRequirements ?? guest.hasDietaryRequirements) || false, e.target.value)}
                      disabled={isPastDeadline}
                      placeholder="Please specify your dietary requirements..."
                      className="input-field"
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        fontSize: 'clamp(0.85em, 2.5vw, 0.95em)',
                        fontFamily: 'var(--font-body)',
                        resize: 'vertical',
                        padding: '10px',
                        marginBottom: '10px'
                      }}
                    />
                    {!isPastDeadline && (
                      <button
                        onClick={() => handleDietaryRequirementsSubmit(guest.id)}
                        className="button-primary"
                        style={{
                          fontSize: 'clamp(0.85em, 2.5vw, 0.95em)',
                          padding: '8px 16px'
                        }}
                      >
                        Save Dietary Requirements
                      </button>
                    )}
                  </>
                )}
                {isPastDeadline && guest.dietaryRequirements && (
                  <div style={{ 
                    fontStyle: 'italic', 
                    paddingTop: '6px',
                    fontSize: 'clamp(0.85em, 2.5vw, 0.95em)',
                    wordBreak: 'break-word',
                    fontFamily: 'var(--font-body)',
                    color: 'var(--text-secondary)'
                  }}>
                    {guest.dietaryRequirements}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!isPastDeadline && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '40px',
          padding: '0 10px'
        }}>
          <button
            onClick={() => {
              setShowToast(true);
              setTimeout(() => {
                setShowToast(false);
              }, 3000);
            }}
            className="button-primary"
            style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
              padding: '14px 32px',
              fontWeight: '600'
            }}
          >
            Submit Menu Choices
          </button>
        </div>
      )}
      </>
      )}
      {showToast && (
        <div
          style={{
            position: 'fixed',
            bottom: '30px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'var(--button-success)',
            color: 'var(--text-primary)',
            padding: '16px 32px',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            zIndex: 10000,
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
            fontWeight: '500',
            animation: 'slideUpFromBottom 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)'
          }}
        >
          Thank you, your menu choices have been received.
        </div>
      )}
    </div>
    </>
  );
}

export default MenuChoices;